import { Component, OnInit, OnDestroy, Input, OnChanges, SimpleChanges, Inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser, ViewportScroller } from '@angular/common';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { NewsService } from '../../core/services/news.service';
import { News } from '../../core/models/news.interface';
import { ImageService } from '../../core/services/image.service';
import { DomSanitizer, SafeHtml, SafeResourceUrl } from '@angular/platform-browser';
import { SeoService } from '../../core/services/seo.service';

@Component({
  selector: 'app-news-viewer',
  standalone: true,
  templateUrl: './news-viewer.component.html',
  styleUrls: ['./news-viewer.component.scss'],
  imports: [CommonModule, RouterModule]
})
export class NewsViewerComponent implements OnInit, OnDestroy {
  @Input() article: News | null = null;
  @Input() previewMode: boolean = false;
  loading = true;
  error = false;
  relatedArticles: News[] = [];
  latestNews: News[] = [];
  urlActual: string = '';
  pieDeVideo: string | null = null;
  urlVideo: string | null = null;

  // Variables para el video
  videoUrl: SafeResourceUrl | null = null;
  
  // Propiedades para controlar la visualización de la imagen
  heroImageStyle: any = {};
  showHeroImage: boolean = false;
  heroImageUrl: string = '';

  private ngUnsubscribe = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private newsService: NewsService,
    private imageService: ImageService,
    private sanitizer: DomSanitizer,
    private seoService: SeoService,
    private viewportScroller: ViewportScroller,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  ngOnInit(): void {
    if (!this.previewMode) {
      if (isPlatformBrowser(this.platformId)) {
        this.urlActual = window.location.href;
      }
      
      // Suscribirse a los cambios de ruta
      this.route.params
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe(params => {
          if (params['slug']) {
            // Desplazar al inicio de la página cuando se carga una nueva noticia
            if (isPlatformBrowser(this.platformId)) {
              this.viewportScroller.scrollToPosition([0, 0]);
            }
            this.loadArticle(params['slug']);
          }
        });
    } else {
      // Si estamos en modo vista previa, no necesitamos cargar nada
      this.loading = false;
      this.error = false;
      
      // Configurar el estilo de la imagen principal
      this.setHeroImageStyle();
      
      // Verificar si la imagen existe
      const imageSource = this.article?.imageUrl || this.article?.imagePreview;
      if (imageSource) {
        this.checkImageExists(imageSource);
      }
    }
  }


  loadArticle(slug: string): void {
    this.loading = true;
    this.error = false;

    this.newsService.getArticleBySlug(slug).pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (article) => {
        this.article = article;
        this.seoService.updateNewsMetaTags(article);
        this.loading = false;
        this.loadRelatedArticles();
        this.loadLatestNews();
        
        // Configurar el estilo de la imagen principal
        this.setHeroImageStyle();
        
        // Verificar si la imagen existe
        const imageSource = article?.imageUrl || article?.imagePreview;
        if (imageSource) {
          this.checkImageExists(imageSource);
        }
      },
      error: (error) => {
        console.error('Error loading article:', error);
        this.loading = false;
        this.error = true;
      }
    });
  }

  loadRelatedArticles(): void {
    if (this.article?.id && this.article?.category) {
      this.newsService.getRelatedArticles(this.article.category, this.article.id, 3).pipe(takeUntil(this.ngUnsubscribe)).subscribe({
        next: (articles) => {
          this.relatedArticles = articles;
          
          // Verificar las imágenes de los artículos relacionados
          this.relatedArticles.forEach(article => {
            const imageSource = this.getImageSrc(article);if (imageSource && isPlatformBrowser(this.platformId)) {
              this.checkImageExists(imageSource);
            }
          });
        },
        error: (error) => {
          console.error('Error loading related articles:', error);
        }
      });
    }
  }

  loadLatestNews(): void {
    this.newsService.getLatestArticles(4).pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (articles) => {
        // Filtrar el artículo actual de las últimas noticias
        this.latestNews = articles.filter(article => article.id !== this.article?.id);
        
        
        // Verificar las imágenes de las últimas noticias
        this.latestNews.forEach(article => {
          const imageSource = this.getImageSrc(article);
          if (imageSource && isPlatformBrowser(this.platformId)) {
            this.checkImageExists(imageSource);
          }
        });
      },
      error: (error) => {
        console.error('Error loading latest news:', error);
      }
    });
  }

  getFormattedDate(date: any): string {
    if (!date) return '';
    const options: Intl.DateTimeFormatOptions = { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(date.seconds ? date.seconds * 1000 : date).toLocaleDateString('es-ES', options);
  }

  getImageSrc(article: News, preferWebP: boolean = true): string | null {
    return this.imageService.getImageSrc(article, preferWebP);
  }

  /**
   * Obtiene la URL de la imagen con caché optimizado para el artículo actual
   * @param preferWebP Si se prefiere formato WebP cuando esté disponible
   * @returns La URL de la imagen optimizada desde caché
   */
  getImageSrcWithCache(preferWebP: boolean = true): string | null {
    if (!this.article) return null;
    return this.imageService.getImageSrc(this.article, preferWebP);
  }

  sanitizeHtml(html: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  shareContent(platform: string): void {
    if (!isPlatformBrowser(this.platformId)) return;

    let shareUrl = '';
    const title = this.article?.title || '';
    const url = encodeURIComponent(this.urlActual);

    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${url}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
        break;
      case 'whatsapp':
        shareUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(title + ' ' + this.urlActual)}`;
        break;
    }

    if (shareUrl) {
      window.open(shareUrl, '_blank', 'width=600,height=400');
    }
  }

  // Método para verificar si una imagen existe
  checkImageExists(url: string): void {
    if (!url) return;
    this.imageService.checkImageExists(url);
  }

  // Método para configurar el estilo de la imagen principal
  setHeroImageStyle(): void {
    if (!this.article) {
      this.heroImageStyle = {
        'background-color': '#f0f0f0'
      };
      this.showHeroImage = false;
      return;
    }
    
    // Usar el sistema de caché de imágenes optimizado
    const cachedImageUrl = this.getImageSrcWithCache(true);
    
    if (cachedImageUrl && isPlatformBrowser(this.platformId)) {
      
      // Configurar la URL de la imagen optimizada desde caché
      this.heroImageUrl = cachedImageUrl;
      this.showHeroImage = true;
      
      // También configurar el estilo de fondo como respaldo
      this.heroImageStyle = {
        'background-color': '#f0f0f0'
      };
      
      // Verificar si la imagen existe (ya optimizada desde caché)
      this.checkImageExists(cachedImageUrl);
    } else {
      this.heroImageStyle = {
        'background-color': '#f0f0f0'
      };
      this.showHeroImage = false;
    }
  }

  // Método para obtener la URL de la imagen optimizada desde caché
  getImageUrl(article: News): string {
    const cachedImageUrl = this.getImageSrc(article, true);
    return cachedImageUrl ? `url(${cachedImageUrl})` : 'none';
  }

  // Detecta si la URL es de YouTube
  isYouTubeUrl(url: string): boolean {
    return url.includes('youtube.com') || url.includes('youtu.be');
  }

  // Detecta si la URL es de Facebook
  isFacebookUrl(url: string): boolean {
    return url.includes('facebook.com');
  }

  // Convierte la URL en una URL segura para incrustar
  getSafeVideoUrl(url: string): SafeResourceUrl {
    let embedUrl = url;

    // Transformación de la URL según la plataforma
    if (this.isYouTubeUrl(url)) {
      // Manejar diferentes formatos de URL de YouTube
      if (url.includes('youtube.com/watch?v=')) {
        const videoId = url.split('watch?v=')[1].split('&')[0];
        embedUrl = `https://www.youtube.com/embed/${videoId}`;
      } else if (url.includes('youtu.be/')) {
        const videoId = url.split('youtu.be/')[1].split('?')[0];
        embedUrl = `https://www.youtube.com/embed/${videoId}`;
      } else if (url.includes('youtube.com/embed/')) {
        // Ya está en formato embed
        embedUrl = url;
      }
    } else if (this.isFacebookUrl(url)) {
      embedUrl = `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}`;
    }
    // Puedes añadir más reglas para otras plataformas

    return this.sanitizer.bypassSecurityTrustResourceUrl(embedUrl);
  }

  // Getter para el estilo dinámico del subtítulo
  get subtitleStyle(): { [key: string]: string } {
    const firstParagraphHasTitle = this.article?.paragraphs?.[0]?.title;
    // Si el primer párrafo tiene título, usar margen pequeño, si no, margen más grande
    return { 'margin-bottom': firstParagraphHasTitle ? '0.50rem' : '0.75rem' }; 
  }

  ngOnDestroy(): void {
    this.seoService.clearArticleMetaTags();
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
